import React, { useState } from "react";
import { useLocation } from "wouter";
import { Box, Stack } from "@mui/material";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import MyContainer from "@/components/MyContainer";
import { SaveButton } from "@/components/ui/buttons/save-button";
import CustomerForm from "@/pages/customer/form/customer-form";
import type { CustomerFormValues } from "@/api/types";
import { customerSchema } from "@/api/types";
import { useCreateCustomer } from "@/services/customer-service";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";

export default function CreateCustomerPage() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const createCustomerMutation = useCreateCustomer();

  const form = useForm<CustomerFormValues>({
    resolver: zodResolver(customerSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      phone: "",
      address: "",
      saleChannel: "Facebook",
      notTransfer: false,
    },
  });

  const handleSubmit = async (values: CustomerFormValues) => {
    const response = await createCustomerMutation.execute(values);

    if (!response.success || !response.data) {
      throw new Error(response.message || "Failed to create customer");
    }

    toast({
      title: "Customer created",
      description: "The customer has been added successfully.",
    });

    return response.data;
  };

  const handleSuccess = () => {
    setLocation("/customers");
  };

  const handleCancel = () => {
    setLocation("/customers");
  };

  const handleSave = () => {
    form.handleSubmit(handleSubmit)();
  };

  return (
    <MyContainer
      title="Create customer"
      subtitle="Add a new customer to your audience."
      actions={
        <SaveButton
          disabled={form.formState.isSubmitting}
          handleSave={handleSave}
        />
      }
      footer={
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Stack direction="row" spacing={1}>
            <Button variant="outline" size="sm" onClick={handleCancel}>
              Cancel
            </Button>
            <SaveButton
              disabled={form.formState.isSubmitting}
              handleSave={handleSave}
            />
          </Stack>
        </Box>
      }
    >
      <CustomerForm
        form={form}
        mode="create"
        onSubmit={handleSubmit}
        onSuccess={handleSuccess}
        onCancel={handleCancel}
      />
    </MyContainer>
  );
}
